import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import Box from "ds/components/Box";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerForm from "ds/components/DrawerNew/Form";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { pluralize } from "shared/Pluralize";
import AssignRoleForm from "components/AssignRoleForm";
import { AssignRoleFormAddFields, AssignRoleFormEditFields } from "components/AssignRoleForm/types";
import { CREATE_FORM_ID } from "components/AssignRoleForm/constants";

import {
  USER_GROUP_ROLE_BINDING_BATCH_CREATE,
  USER_GROUP_ROLE_BINDING_UPDATE,
  USER_GROUP_ROLE_BINDINGS_WITH_ROLES,
} from "./gql";
import { showUnassignConfirmation } from "./UnassignConfirmation";
import { UserManagementActivationStatus } from "../../types";

type AssignRoleDrawerProps = {
  userGroupId: string;
  activationStatus: UserManagementActivationStatus;
};

const refetchQueries = ["UserGroupRoleBindingsWithRoles"];

const AssignRoleDrawer = createDrawer(
  ({ userGroupId, activationStatus }: AssignRoleDrawerProps) => {
    const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({
      [CREATE_FORM_ID]: false,
    });

    const { onError, reportSuccess } = useTypedContext(FlashContext);

    const setIsDirtyHandler = useCallback(
      (formId: string, value: boolean) => setDirtyFields((prev) => ({ ...prev, [formId]: value })),
      []
    );

    const { data, loading } = useQuery(USER_GROUP_ROLE_BINDINGS_WITH_ROLES, {
      variables: {
        id: userGroupId,
      },
    });

    const assignedRoles = useMemo(
      () =>
        data?.managedUserGroup.userRoleBindings.map((binding) => ({
          id: binding.id,
          roleID: binding.roleID,
          spaceID: binding.space.id,
          spaceName: binding.space.name,
        })) || [],
      [data?.managedUserGroup.userRoleBindings]
    );

    const [createBatchRoleBinding, { loading: createBatchRoleBindingLoading }] = useMutation(
      USER_GROUP_ROLE_BINDING_BATCH_CREATE,
      {
        refetchQueries,
        awaitRefetchQueries: true,
      }
    );

    const onCreate = useCallback(
      (formData: AssignRoleFormAddFields) => {
        const roleBindings = formData.roleIds.map((roleId) => ({
          roleID: roleId,
          spaceID: formData.space,
          userGroupID: userGroupId,
        }));

        return createBatchRoleBinding({
          variables: {
            input: {
              bindings: roleBindings,
            },
          },
        })
          .then(() => {
            reportSuccess({
              message: `${pluralize("Role", roleBindings.length)} successfully assigned`,
            });
          })
          .catch(onError);
      },
      [createBatchRoleBinding, onError, reportSuccess, userGroupId]
    );

    const [updateRoleBinding] = useMutation(USER_GROUP_ROLE_BINDING_UPDATE, {
      refetchQueries,
      awaitRefetchQueries: true,
    });

    const onEdit = useCallback(
      (formData: AssignRoleFormEditFields, roleAssignmentId: string) => {
        updateRoleBinding({
          variables: {
            id: roleAssignmentId,
            input: {
              roleID: formData.roleId,
              spaceID: formData.space,
              userGroupID: userGroupId,
            },
          },
        })
          .then(() => {
            reportSuccess({ message: "Role assignment successfully updated" });
            setIsDirtyHandler(roleAssignmentId, false);
          })
          .catch(onError);
      },
      [updateRoleBinding, onError, reportSuccess, setIsDirtyHandler, userGroupId]
    );

    const availableRoles = useMemo(() => data?.roles || [], [data?.roles]);
    const isDirty = useMemo(
      () => !!Object.values(dirtyFields).filter(Boolean).length,
      [dirtyFields]
    );

    const onDelete = useCallback(
      (roleAssignmentId: string, roleName: string) =>
        showUnassignConfirmation({
          id: roleAssignmentId,
          name: roleName,
          refetchQueries,
          isLastRole: assignedRoles.length === 1,
          activationStatus,
        }),
      [activationStatus, assignedRoles.length]
    );

    return (
      <DrawerForm isDirty={isDirty}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Manage roles" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody fullHeight>
          <Box direction="column" gap="large">
            <AssignRoleForm
              onCreate={onCreate}
              onEdit={onEdit}
              setDirtyFields={setIsDirtyHandler}
              availableRoles={availableRoles}
              assignedRoles={assignedRoles}
              loading={loading}
              onDelete={onDelete}
              createLoading={createBatchRoleBindingLoading}
            />
          </Box>
        </DrawerBody>
      </DrawerForm>
    );
  }
);

export const showAssignRoleDrawer = createDrawerTrigger(AssignRoleDrawer);
